import { computed, defineComponent, onMounted, onUnmounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { PscAction, PscGetter } from '@/store/psc/psc.module';
import { PscSchema } from '@/store/psc/psc.validator';
import { PscResult } from '@/store/psc/psc.model';
import Datepicker from '@vuepic/vue-datepicker';
import LoadingComponent from '@/components/loading/Loading.vue';

export default defineComponent({
  name: 'PscPage',
  components: {
    Datepicker,
    LoadingComponent,
  },
  setup: () => {
    const store = useStore();
    const pscs = computed<PscResult[]>(() => store.getters[PscGetter.ALL]);

    interface Result {
      total: number;
      inprogress: number;
      done: number;
    }
    const result = computed<Result>(() => ({
      total: pscs.value.reduce((total, m) => total + m.count_total, 0),
      inprogress: pscs.value.reduce((total, m) => total + m.count_inprogress, 0),
      done: pscs.value.reduce((total, m) => total + m.count_done, 0)
    }));

    const datatable = ref<HTMLTableElement>();
    const dtInstance = ref();

    // form
    interface Form {
      dateFrom?: Date | null;
      dateTo?: Date | null;
    }
    const formData = reactive<Form>({
      dateFrom: null,
      dateTo: null,
    });
    const validator = computed(() => PscSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      dateFrom: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'dateFrom')),
      dateTo: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'dateTo')),
    });
    const isValid = computed(() => !errors.dateFrom && !errors.dateTo);
    const isLoading = computed<boolean>(() => store.getters[PscGetter.STATUS] === 'init' || store.getters[PscGetter.STATUS] === 'loading');
    const submited = ref(false);

    onMounted(() => {
      store.dispatch(PscAction.CLEAR)
        .then(() => initialDataTable());
    });

    onUnmounted(() => {
      dtInstance.value?.destroy();
    });

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // submit form
    const searchClick = () => {
      submited.value = true;

      if (isValid.value) {
        store.dispatch(PscAction.FETCH, formData)
          .then(() => {
            initialDataTable();
            unsubmit();
          });
      }
    };

    const initialDataTable = () => {
      const dt = datatable.value as HTMLTableElement;
      if (!$.fn.dataTable.isDataTable(dt)) {
        dtInstance.value = $(dt).DataTable({
          language: {
            emptyTable: 'ไม่พบข้อมูล',
            info: 'แสดง _START_ ถึง _END_ จากจำนวนทั้งหมด _TOTAL_',
            infoEmpty: 'แสดง 0 ถึง 0 จากจำนวนทั้งหมด 0',
            infoFiltered: '(ค้นหาจากจำนวนทั้งหมด _MAX_)',
            lengthMenu: 'แสดงข้อมูล _MENU_',
            paginate: {
              next: 'ถัดไป',
              previous: 'ก่อนหน้า',
              first: 'First',
              last: 'Last'
            },
            search: 'ค้นหา:',
            zeroRecords: 'ไม่พบข้อมูลที่ค้นหา',
          }
        });
      }
    };

    return {
      pscs,
      result,
      formData,
      datatable,
      isLoading,
      errors,
      submited,
      searchClick,
      unsubmit,
    };
  }
});
